import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';

interface SearchableSelectOption {
  id: string;
  name: string;
}

interface SearchableSelectProps {
  /** Selected option id, or '' for none. */
  value: string;
  /** Called with the chosen option id, or '' when "None" is picked. */
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** When true, prepend a "None" entry that clears the selection. */
  allowNone?: boolean;
  noneLabel?: string;
  disabled?: boolean;
  ariaInvalid?: boolean;
  /** Accessible name for the trigger when the visible label is only a placeholder. */
  ariaLabel?: string;
  modal?: boolean;
  className?: string;
}

/**
 * Single-select dropdown with type-to-filter search, built on Popover + cmdk Command.
 * Drop-in alternative to a plain Radix `Select` for long, static `{ id, name }` lists
 * (locations, departments) where scroll-only selection does not scale (HW-593).
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results.',
  allowNone = true,
  noneLabel = 'None',
  disabled = false,
  ariaInvalid = false,
  ariaLabel,
  modal = false,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => options.find((o) => o.id === value), [options, value]);

  const handleSelect = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <Popover modal={modal} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn('h-9 w-full justify-between font-normal cursor-pointer', className)}
        >
          <span
            className={cn('truncate', !selected && 'text-muted-foreground')}
            title={selected?.name}
          >
            {selected ? selected.name : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty className="py-4 text-sm text-muted-foreground text-center">
              {emptyText}
            </CommandEmpty>
            <CommandGroup>
              {allowNone && (
                <CommandItem
                  value={noneLabel}
                  onSelect={() => handleSelect('')}
                  className="cursor-pointer text-muted-foreground"
                >
                  <Check
                    className={cn('mr-2 h-4 w-4 shrink-0', value ? 'opacity-0' : 'opacity-100')}
                  />
                  {noneLabel}
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.name}
                  onSelect={() => handleSelect(option.id)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      value === option.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="truncate" title={option.name}>
                    {option.name}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
